import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Button, Tabs, Tab } from 'react-bootstrap';
import {
  Folder,
  Award,
  BookOpen,
  Sparkles,
  Building2,
  Users,
  GraduationCap,
  Palette,
  Mic,
  FileText,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';
import { jubileeStorageService } from '../services/jubileeStorageService';
import JubileeVideoPlayerModal from '../viewers/JubileeVideoPlayerModal';
import JubileeImageLightbox from '../viewers/JubileeImageLightbox';
import JubileeDocumentViewerModal from '../viewers/JubileeDocumentViewerModal';

const ICON_MAP = {
  Award: Award,
  BookOpen: BookOpen,
  Sparkles: Sparkles,
  Building2: Building2,
  Users: Users,
  GraduationCap: GraduationCap,
  Palette: Palette,
  Mic: Mic,
  FileText: FileText,
};

export default function JubileeCollectionsBrowse({ onPlayAudioTrack }) {
  const [selectedCatId, setSelectedCatId] = useState(null);
  const [activeMediaTab, setActiveMediaTab] = useState('all');

  // Modals state
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);

  const categories = useMemo(() => jubileeStorageService.getCategories(), []);
  const allMedia = useMemo(() => jubileeStorageService.getAllMedia(), []);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCatId),
    [categories, selectedCatId]
  );

  const categoryMedia = useMemo(() => {
    if (!selectedCatId) return { videos: [], audios: [], images: [], documents: [] };
    const items = allMedia.filter((m) => m.categoryId === selectedCatId);
    return {
      videos: items.filter((m) => m.mediaType === 'video'),
      audios: items.filter((m) => m.mediaType === 'audio'),
      images: items.filter((m) => m.mediaType === 'image'),
      documents: items.filter((m) => m.mediaType === 'document'),
      total: items.length,
    };
  }, [allMedia, selectedCatId]);

  return (
    <div className="py-4">
      <Container>
        {!selectedCatId ? (
          /* ================= CATEGORY CARDS DIRECTORY ================= */
          <div>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4 pb-3 border-bottom">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <div className="p-2 rounded-3 text-white d-inline-flex" style={{ background: '#3b82f6' }}>
                    <Folder size={22} />
                  </div>
                  <h2 className="fw-bold m-0 text-slate-900" style={{ letterSpacing: '-0.5px' }}>
                    Archival Collections & Themes
                  </h2>
                </div>
                <p className="text-muted small m-0">
                  Browse archival materials grouped by thematic heritage collections and historical series.
                </p>
              </div>

              <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-3 py-2 rounded-pill fw-semibold">
                {categories.length} Collections
              </span>
            </div>

            <Row className="g-4">
              {categories.map((cat) => {
                const IconComponent = ICON_MAP[cat.icon] || Folder;
                const items = allMedia.filter((m) => m.categoryId === cat.id);
                const vidCount = items.filter((m) => m.mediaType === 'video').length;
                const audCount = items.filter((m) => m.mediaType === 'audio').length;
                const imgCount = items.filter((m) => m.mediaType === 'image').length;
                const docCount = items.filter((m) => m.mediaType === 'document').length;

                return (
                  <Col key={cat.id} xs={12} sm={6} lg={4}>
                    <div
                      className="jubilee-media-card cursor-pointer p-4 h-100"
                      onClick={() => setSelectedCatId(cat.id)}
                    >
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div
                          className="rounded-3 p-3 d-inline-flex"
                          style={{ background: '#eff6ff', color: '#2563eb' }}
                        >
                          <IconComponent size={26} />
                        </div>
                        <span className="badge bg-light text-secondary border">
                          {items.length} Records
                        </span>
                      </div>

                      <h5 className="fw-bold text-slate-900 mb-2">{cat.name}</h5>
                      <p className="text-muted small mb-3 flex-grow-1" style={{ lineHeight: '1.5' }}>
                        {cat.description}
                      </p>

                      {/* Media breakdown badges */}
                      <div className="d-flex flex-wrap gap-1 mb-3">
                        {vidCount > 0 && <span className="badge bg-danger bg-opacity-10 text-danger text-xs">{vidCount} Videos</span>}
                        {audCount > 0 && <span className="badge bg-indigo bg-opacity-10 text-primary text-xs">{audCount} Audios</span>}
                        {imgCount > 0 && <span className="badge bg-warning bg-opacity-10 text-dark text-xs">{imgCount} Photos</span>}
                        {docCount > 0 && <span className="badge bg-success bg-opacity-10 text-success text-xs">{docCount} Docs</span>}
                      </div>

                      <div className="d-flex align-items-center justify-content-between pt-2 border-top">
                        <span className="text-muted small">Explore Collection</span>
                        <ChevronRight size={16} className="text-primary" />
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        ) : (
          /* ================= DEDICATED COLLECTION DETAIL VIEW ================= */
          <div>
            <Button
              variant="link"
              className="text-primary p-0 mb-3 d-inline-flex align-items-center gap-1 text-decoration-none fw-semibold"
              onClick={() => setSelectedCatId(null)}
            >
              <ArrowLeft size={16} /> Back to All Collections
            </Button>

            <div className="bg-white p-4 rounded-4 shadow-sm border mb-4">
              <div className="d-flex align-items-center gap-3 mb-2">
                <div className="p-3 bg-primary bg-opacity-10 text-primary rounded-3">
                  <Folder size={28} />
                </div>
                <div>
                  <h3 className="fw-bold text-slate-900 m-0">{selectedCategory.name}</h3>
                  <p className="text-muted small m-0">{selectedCategory.description}</p>
                </div>
              </div>
            </div>

            {/* Separated Media Tabs for This Collection */}
            <Tabs
              activeKey={activeMediaTab}
              onSelect={(k) => setActiveMediaTab(k)}
              className="mb-4 nav-pills custom-media-tabs"
            >
              <Tab eventKey="all" title={`All Items (${categoryMedia.total})`}>
                <Row className="g-4">
                  {/* Videos */}
                  {categoryMedia.videos.map((v) => (
                    <Col key={v.id} xs={12} sm={6} md={4}>
                      <div
                        className="jubilee-media-card cursor-pointer"
                        onClick={() => {
                          setSelectedVideo(v);
                          setShowVideoModal(true);
                        }}
                      >
                        <div className="media-thumbnail-wrapper">
                          <img src={v.thumbnailUrl} alt={v.title} className="media-thumbnail-img" />
                          <span className="media-type-badge video">Video</span>
                        </div>
                        <div className="media-card-body">
                          <h6 className="media-card-title">{v.title}</h6>
                          <span className="text-danger small fw-semibold">Watch Video →</span>
                        </div>
                      </div>
                    </Col>
                  ))}

                  {/* Documents */}
                  {categoryMedia.documents.map((d) => (
                    <Col key={d.id} xs={12} sm={6} md={4}>
                      <div
                        className="jubilee-media-card cursor-pointer"
                        onClick={() => {
                          setSelectedDoc(d);
                          setShowDocModal(true);
                        }}
                      >
                        <div className="media-thumbnail-wrapper" style={{ height: '160px' }}>
                          <img src={d.thumbnailUrl} alt={d.title} className="media-thumbnail-img" />
                          <span className="media-type-badge document">Document</span>
                        </div>
                        <div className="media-card-body">
                          <h6 className="media-card-title">{d.title}</h6>
                          <span className="text-success small fw-semibold">Read Online →</span>
                        </div>
                      </div>
                    </Col>
                  ))}

                  {/* Photos */}
                  {categoryMedia.images.map((img, idx) => (
                    <Col key={img.id} xs={12} sm={6} md={4}>
                      <div
                        className="jubilee-media-card cursor-pointer"
                        onClick={() => {
                          setSelectedImageIndex(idx);
                          setShowImageModal(true);
                        }}
                      >
                        <div className="media-thumbnail-wrapper" style={{ height: '160px' }}>
                          <img src={img.thumbnailUrl || img.fileUrl} alt={img.title} className="media-thumbnail-img" />
                          <span className="media-type-badge image">Photo</span>
                        </div>
                        <div className="media-card-body">
                          <h6 className="media-card-title">{img.title}</h6>
                          <span className="text-warning small fw-semibold">View Photo →</span>
                        </div>
                      </div>
                    </Col>
                  ))}

                  {/* Audio */}
                  {categoryMedia.audios.map((aud) => (
                    <Col key={aud.id} xs={12}>
                      <div className="audio-card p-3">
                        <img src={aud.thumbnailUrl} alt={aud.title} className="audio-cover-art" />
                        <div className="flex-grow-1">
                          <h6 className="fw-bold m-0">{aud.title}</h6>
                          <p className="text-muted small m-0">{aud.speaker}</p>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          className="rounded-pill"
                          onClick={() => onPlayAudioTrack && onPlayAudioTrack(aud)}
                        >
                          Listen
                        </Button>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Tab>

              <Tab eventKey="videos" title={`Videos (${categoryMedia.videos.length})`}>
                <Row className="g-4">
                  {categoryMedia.videos.map((v) => (
                    <Col key={v.id} xs={12} sm={6} md={4}>
                      <div
                        className="jubilee-media-card cursor-pointer"
                        onClick={() => {
                          setSelectedVideo(v);
                          setShowVideoModal(true);
                        }}
                      >
                        <div className="media-thumbnail-wrapper">
                          <img src={v.thumbnailUrl} alt={v.title} className="media-thumbnail-img" />
                        </div>
                        <div className="media-card-body">
                          <h6 className="media-card-title">{v.title}</h6>
                          <span className="text-danger small">Watch Video →</span>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Tab>

              <Tab eventKey="documents" title={`Documents (${categoryMedia.documents.length})`}>
                <Row className="g-4">
                  {categoryMedia.documents.map((d) => (
                    <Col key={d.id} xs={12} sm={6} md={4}>
                      <div
                        className="jubilee-media-card cursor-pointer"
                        onClick={() => {
                          setSelectedDoc(d);
                          setShowDocModal(true);
                        }}
                      >
                        <div className="media-thumbnail-wrapper" style={{ height: '160px' }}>
                          <img src={d.thumbnailUrl} alt={d.title} className="media-thumbnail-img" />
                        </div>
                        <div className="media-card-body">
                          <h6 className="media-card-title">{d.title}</h6>
                          <span className="text-success small">Read Online →</span>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Tab>
            </Tabs>
          </div>
        )}
      </Container>

      {/* Media Preview Modals */}
      <JubileeVideoPlayerModal
        video={selectedVideo}
        show={showVideoModal}
        onHide={() => setShowVideoModal(false)}
        allVideos={allMedia.filter((m) => m.mediaType === 'video')}
      />

      <JubileeImageLightbox
        images={categoryMedia.images}
        selectedIndex={selectedImageIndex}
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        onSelectIndex={(i) => setSelectedImageIndex(i)}
      />

      <JubileeDocumentViewerModal
        document={selectedDoc}
        show={showDocModal}
        onHide={() => setShowDocModal(false)}
      />
    </div>
  );
}
